import React from "react";
import { Box, Camera, GitMerge, Grid3X3, MapPin, Pointer, Spline, Zap } from "lucide-react";
import { SHORTCUT_ACTIONS, withShortcutLabel } from "../../shortcuts/defaultBindings";
import { EllipsoidIcon, EntireObjectIcon, ExplosionIcon, JointIcon } from "../ui/Icons";

function PriorityBadgeIcon({ className, BadgeIcon, badgeClassName = "" }) {
  return (
    <span className={`relative inline-flex ${className}`} aria-hidden>
      <Pointer className="h-full w-full" />
      <BadgeIcon className={`absolute -right-0.5 -top-1 h-3 w-3 ${badgeClassName}`} />
    </span>
  );
}

function BodyPriorityIcon({ className }) {
  return <PriorityBadgeIcon className={className} BadgeIcon={Box} />;
}

function ObjectPriorityIcon({ className }) {
  return <PriorityBadgeIcon className={className} BadgeIcon={EntireObjectIcon} />;
}

function JointPriorityIcon({ className }) {
  return <PriorityBadgeIcon className={className} BadgeIcon={JointIcon} />;
}

function VisualsPriorityIcon({ className }) {
  return <PriorityBadgeIcon className={className} BadgeIcon={Grid3X3} />;
}

function CollisionPriorityIcon({ className }) {
  return <PriorityBadgeIcon className={className} BadgeIcon={ExplosionIcon} />;
}

function SitePriorityIcon({ className }) {
  return <PriorityBadgeIcon className={className} BadgeIcon={MapPin} />;
}

function InertialPriorityIcon({ className }) {
  return <PriorityBadgeIcon className={className} BadgeIcon={EllipsoidIcon} />;
}

function ConstraintPriorityIcon({ className }) {
  return <PriorityBadgeIcon className={className} BadgeIcon={GitMerge} />;
}

function TendonPriorityIcon({ className }) {
  return <PriorityBadgeIcon className={className} BadgeIcon={Spline} />;
}

const priorityItems = [
  {
    key: "object",
    presenceKey: "object",
    label: "Select Objects",
    Icon: EntireObjectIcon,
    PrimaryIcon: ObjectPriorityIcon,
  },
  {
    key: "body",
    presenceKey: "body",
    alwaysVisible: true,
    label: "Select Bodies",
    Icon: Box,
    PrimaryIcon: BodyPriorityIcon,
  },
  {
    key: "visuals",
    presenceKey: "visual",
    alwaysVisible: true,
    storeKeys: ["mesh", "visualGeom"],
    label: "Select Visuals",
    Icon: Grid3X3,
    PrimaryIcon: VisualsPriorityIcon,
  },
  {
    key: "collision",
    presenceKey: "collision",
    label: "Select Collisions",
    Icon: ExplosionIcon,
    PrimaryIcon: CollisionPriorityIcon,
  },
  {
    key: "joint",
    presenceKey: "joint",
    label: "Select Joints",
    Icon: JointIcon,
    PrimaryIcon: JointPriorityIcon,
  },
  {
    key: "site",
    presenceKey: "site",
    label: "Select Sites",
    Icon: MapPin,
    PrimaryIcon: SitePriorityIcon,
  },
  {
    key: "inertial",
    presenceKey: "inertial",
    label: "Select Inertials",
    Icon: EllipsoidIcon,
    PrimaryIcon: InertialPriorityIcon,
  },
  {
    key: "constraint",
    presenceKey: "constraint",
    label: "Select Constraints",
    Icon: GitMerge,
    PrimaryIcon: ConstraintPriorityIcon,
  },
  {
    key: "tendon",
    presenceKey: "tendon",
    label: "Select Tendons",
    Icon: Spline,
    PrimaryIcon: TendonPriorityIcon,
  },
];

const visibilityItems = [
  {
    key: "showVisual",
    presenceKey: "visual",
    alwaysVisible: true,
    label: withShortcutLabel("Visuals", SHORTCUT_ACTIONS.VIEWER_LAYER_TOGGLE_VISUAL),
    Icon: Grid3X3,
    action: "toggleShowVisual",
  },
  {
    key: "showCollision",
    presenceKey: "collision",
    label: withShortcutLabel("Collisions", SHORTCUT_ACTIONS.VIEWER_LAYER_TOGGLE_COLLISION),
    Icon: ExplosionIcon,
    action: "toggleShowCollision",
  },
  {
    key: "showJoints",
    presenceKey: "joint",
    label: withShortcutLabel("Joint markers", SHORTCUT_ACTIONS.VIEWER_LAYER_TOGGLE_JOINTS),
    Icon: JointIcon,
    action: "toggleShowJoints",
  },
  {
    key: "showSites",
    presenceKey: "site",
    label: withShortcutLabel("Sites", SHORTCUT_ACTIONS.VIEWER_LAYER_TOGGLE_SITES),
    Icon: MapPin,
    action: "toggleShowSites",
  },
  {
    key: "showInertials",
    presenceKey: "inertial",
    label: withShortcutLabel("Inertia ellipsoids", SHORTCUT_ACTIONS.VIEWER_LAYER_TOGGLE_INERTIALS),
    Icon: EllipsoidIcon,
    action: "toggleShowInertials",
  },
  {
    key: "showCameras",
    presenceKey: "camera",
    label: "Cameras",
    Icon: Camera,
    action: "toggleShowCameras",
  },
  {
    key: "showConstraintMarkers",
    presenceKey: "constraint",
    label: withShortcutLabel(
      "Constraints",
      SHORTCUT_ACTIONS.VIEWER_LAYER_TOGGLE_CONSTRAINT_MARKERS
    ),
    Icon: GitMerge,
    visibilityKind: "equality",
  },
  {
    key: "showTendonMarkers",
    presenceKey: "tendon",
    label: withShortcutLabel("Tendons", SHORTCUT_ACTIONS.VIEWER_LAYER_TOGGLE_TENDON_MARKERS),
    Icon: Spline,
    visibilityKind: "tendon",
  },
  {
    key: "showActuators",
    presenceKey: "actuator",
    label: withShortcutLabel("Actuators", SHORTCUT_ACTIONS.VIEWER_LAYER_TOGGLE_ACTUATORS),
    Icon: Zap,
    action: "toggleShowActuators",
  },
];

function getPriorityItemKeys(item) {
  return item.storeKeys ?? [item.key];
}

function hasAnyPrioritySelection(scenePrioritySelection) {
  return Object.values(scenePrioritySelection || {}).some(Boolean);
}

function isPriorityItemActive(item, scenePrioritySelection) {
  const hasSelection = hasAnyPrioritySelection(scenePrioritySelection);
  if (!hasSelection && item.key === "body") return true;
  return getPriorityItemKeys(item).some((key) => !!scenePrioritySelection[key]);
}

export { getPriorityItemKeys, isPriorityItemActive, priorityItems, visibilityItems };
